import { Form, useFormContext } from '@ampol/design-system';
import { CardModalFormNames } from './types/CardModalFormNames';
import { ModalSection } from './ModalSection';
import { useSelector } from 'react-redux';
import RsaTermsAndConditionsModal from '../rsa-terms-and-conditions-modal/rsa-terms-and-conditions-modal';
import Vas from 'constants/vas-codes';
import { useSelectAccountDefaults, useSelectCardModalMeta } from 'selectors/selectors';
import { getCardStatus } from 'utilities/card-utilities';
import Env from 'Env';

interface CardModalProductsAndServicesProps {
  readOnly?: boolean;
  white?: boolean;
}

export default function CardModalProductsAndServices({
  readOnly = false,
  white = false,
}: CardModalProductsAndServicesProps) {
  const meta = useSelectCardModalMeta();
  const { vasRoadSideAssistFeeHeavy, vasRoadSideAssistFeePassenger, vasRoadSideAssistFeeTruck, vasFuelPayAccountCode } =
    useSelectAccountDefaults();
  const shouldShowRoadsideAssistance = meta?.vasRsaAccountCode !== Vas.RSA_NC;
  const shouldShowServiceAndRepairs = meta?.vasSnrAccountCode !== Vas.SNR_NC;
  const shouldEnableFuelPay = vasFuelPayAccountCode === Vas.FP_NC;
  const isStatusCardProcessing = getCardStatus() === 'Processing';
  const isEvWay4Enabled = typeof Env.getIsEvWay4Enabled === 'function' ? Env.getIsEvWay4Enabled() : false;

  const vehicleTypes = useSelector((state: any) => state.getVehicleTypes.vehicleTypes);
  const formContext = useFormContext();
  const vehicleType = formContext.watch(CardModalFormNames.VehicleType);

  const vehicleTypesData = vehicleTypes?.map((item: any) => ({
    label: item.vehicleType,
    value: item.code,
  }));

  // eslint-disable-next-line no-unused-vars
  const getRemoteRoadsideAssistFees = () => {
    switch (vehicleType) {
      case 'Passenger':
        return vasRoadSideAssistFeePassenger;
      case 'Heavy Vehicles':
        return vasRoadSideAssistFeeHeavy;
      case 'Trucks':
        return vasRoadSideAssistFeeTruck;
    }
  };

  const getStaticRoadsideAssistFees = () => {
    return vehicleTypes?.find((item: any) => item.code === vehicleType)?.roadSideAssistFees;
  };

  return (
    <div>
      <ModalSection first last>
        <div className={`${white ? 'bg-white' : 'bg-background-light'} p-m`}>
          <span>
            <strong>Products and services * </strong>
            (at least one option is required)
          </span>

          <Form.Checkbox
            name={CardModalFormNames.AllProducts}
            label='All products'
            color='blue'
            className='col-span-2'
            disabled={readOnly}
          />
          <div className={'grid grid-rows-6 grid-flow-col mt-xs gap-xs [&>*]:!m-[0]'}>
            {meta?.fleetLimiters?.map((product) => (
              <Form.Checkbox
                key={product.code}
                name={product.code}
                label={product.desc}
                disabled={
                  !product.available || readOnly || (isEvWay4Enabled && isStatusCardProcessing && product.code === 'EV')
                }
                color='blue'
              />
            ))}
          </div>
        </div>
        <div className='bg-[#e7e9f5] p-m'>
          <strong>Value Added Services</strong>
          <Form.Checkbox
            name={CardModalFormNames.FuelPay}
            label='Enable FuelPay via Ampol app'
            className='!mt-xs'
            color='blue'
            disabled={shouldEnableFuelPay || readOnly}
          />
          <small className='pl-l overflow-hidden block text-sm mt-xs'>
            Link AmpolCard to the Ampol app for seamless payments from your mobile device using FuelPay.
            {isEvWay4Enabled && ' FuelPay is not available for AmpCharge EV charging using AmpolCard.'}
          </small>
          {shouldShowRoadsideAssistance && (
            <>
              <Form.Checkbox
                name={CardModalFormNames.RoadsideAssistance}
                label='Roadside Assistance*'
                className='!mt-xs'
                color='blue'
                disabled={readOnly}
              />
              <Form.Condition fieldName={CardModalFormNames.RoadsideAssistance} condition={(v) => v === true}>
                <div className='pl-l'>
                  {vehicleType && <strong>{getStaticRoadsideAssistFees()}</strong>}
                  <Form.DropdownSearch
                    label={'Vehicle Type'}
                    name={CardModalFormNames.VehicleType}
                    filterOptions={true}
                    data={vehicleTypesData}
                    allowClear={false}
                    placeholder={'Select Vehicle Type'}
                    disabled={readOnly}
                  />
                  <RsaTermsAndConditionsModal vehicleTypeSelected={vehicleType} readOnly={readOnly} />
                </div>
              </Form.Condition>
            </>
          )}
          {shouldShowServiceAndRepairs && (
            <>
              <Form.Checkbox
                name={CardModalFormNames.ServiceAndRepairs}
                label='Services and Repairs*'
                className='!mt-xs'
                color='blue'
                disabled={readOnly}
              />
              <Form.Condition fieldName={CardModalFormNames.ServiceAndRepairs} condition={(v) => v === true}>
                <strong className='ml-l mt-xs block'>$2/mth</strong>
              </Form.Condition>
            </>
          )}
          <br />
          <span>
            <strong>*Incorrect vehicle information may result in services not being provided.</strong>
          </span>
        </div>
      </ModalSection>
    </div>
  );
}
